import type { CompanySearchResult } from '@dash-lite/sdk';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { dashlite } from '@/lib/dashlite';
import { presentError  } from '@/lib/errors';
import type {ErrorPresentation} from '@/lib/errors';

/**
 * Recherche d'entreprises — `GET /companies/search?q=…`.
 *
 * Quatre précautions font toute la différence entre une recherche qui marche
 * et une recherche qui se fait limiter par l'API :
 *
 * 1. **Longueur minimale.** En dessous de trois caractères, on n'appelle pas :
 *    la réponse serait inexploitable et la requête, gaspillée.
 *
 * 2. **Débonce.** La frappe est temporisée (voir `useDebounce`) : « boulangerie »
 *    déclenche un appel, pas onze. La recherche a son propre quota, plus serré
 *    que le reste de l'API.
 *
 * 3. **Requêtes obsolètes ignorées.** Deux appels partis à la suite peuvent
 *    revenir dans le désordre. On ne retient que la réponse correspondant à la
 *    dernière requête émise, sans quoi un résultat périmé pourrait écraser le
 *    bon.
 *
 * 4. **Le pays est implicite.** On n'envoie jamais de code pays : la clé de
 *    site suffit, le backend route vers le bon registre national.
 *
 * En environnement de test, les résultats proviennent d'un jeu d'entreprises
 * fictives. La valeur magique `__unavailable__` force un `503
 * upstream_unavailable`, pratique pour vérifier l'affichage de panne.
 */

export type CompanySearchState = {
    /** Le terme réellement envoyé à l'API (après débonce). */
    submittedQuery: string;
    results: CompanySearchResult[];
    loading: boolean;
    error: ErrorPresentation | null;
    /** `true` quand la recherche a abouti sans aucun résultat. */
    empty: boolean;
};

export const MIN_QUERY_LENGTH = 3;

export function useCompanySearch(query: string, options: { enabled?: boolean } = {}): CompanySearchState {
    const enabled = options.enabled ?? true;
    const debouncedQuery = useDebounce(query.trim(), 350);

    const [state, setState] = useState<CompanySearchState>({
        submittedQuery: '',
        results: [],
        loading: false,
        error: null,
        empty: false,
    });

    /**
     * Numéro de la dernière requête émise. Une réponse qui revient avec un
     * numéro périmé est simplement jetée (précaution n° 3 ci-dessus).
     */
    const requestSequence = useRef(0);

    useEffect(() => {
        if (!enabled || debouncedQuery.length < MIN_QUERY_LENGTH) {
            setState({ submittedQuery: '', results: [], loading: false, error: null, empty: false });

            return;
        }

        const sequence = ++requestSequence.current;

        setState((previous) => ({ ...previous, loading: true, error: null }));

        dashlite
            .searchCompanies(debouncedQuery)
            .then((results) => {
                if (sequence !== requestSequence.current) {
                    return;
                }

                setState({
                    submittedQuery: debouncedQuery,
                    results,
                    loading: false,
                    error: null,
                    empty: results.length === 0,
                });
            })
            .catch((caught: unknown) => {
                if (sequence !== requestSequence.current) {
                    return;
                }

                setState({
                    submittedQuery: debouncedQuery,
                    results: [],
                    loading: false,
                    error: presentError(caught),
                    empty: false,
                });
            });
    }, [debouncedQuery, enabled]);

    return state;
}
